/**
 * useUnsavedChangesGuardCustom
 *
 * Global navigation guard for unsaved form changes.
 *
 * This hook detects *all* navigation attempts when `isDirty = true`
 * and opens a custom confirmation dialog instead of allowing navigation.
 *
 * Intercepts:
 * - navigate() calls (via navigateWrapper)
 * - <a href> clicks (internal links)
 * - browser Back/Forward (popstate)
 * - SPA navigation (history.pushState / replaceState)
 * - manual URL entry
 * - page refresh / tab close (browser built-in dialog)
 *
 * Behavior:
 * - If form is clean → navigation proceeds normally.
 * - If form is dirty → navigation is blocked and modal opens.
 * - After user confirms action → confirmNavigation() continues navigation.
 * - After cancel → navigation is aborted.
 *
 * This hook works globally without modifying existing components:
 * simply place the dialog in the page and forward the hook’s handlers.
 *
 * Returns:
 * - isOpen – whether the dialog should be visible
 * - navigateWrapper(to) – guarded navigation function
 * - confirmNavigation() – proceed after user confirmation
 * - cancelNavigation() – abort navigation attempt
 */

import { useEffect, useRef, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'

export function useUnsavedChangesGuardCustom(isDirty: boolean) {
  const navigate = useNavigate()
  const location = useLocation()
  const [isOpen, setIsOpen] = useState(false)
  const nextLocation = useRef<string | null>(null)
  const unblockRef = useRef(false)

  useEffect(() => {
    if (!isDirty) return

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }

    const originalPush = window.history.pushState
    const originalReplace = window.history.replaceState

    window.history.pushState = function (...args) {
      if (isDirty && !unblockRef.current) {
        const url = args[2]?.toString() || ''
        if (url && url !== location.pathname + location.search) {
          nextLocation.current = url
          setIsOpen(true)
          return
        }
      }
      return originalPush.apply(window.history, args)
    }

    window.history.replaceState = function (...args) {
      if (isDirty && !unblockRef.current) {
        const url = args[2]?.toString() || ''
        if (url && url !== location.pathname + location.search) {
          nextLocation.current = url
          setIsOpen(true)
          return
        }
      }
      return originalReplace.apply(window.history, args)
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.history.pushState = originalPush
      window.history.replaceState = originalReplace
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [isDirty, location.pathname, location.search])

  const confirmNavigation = () => {
    const to = nextLocation.current
    nextLocation.current = null
    setIsOpen(false)
    if (to) {
      unblockRef.current = true
      navigate(to)
      setTimeout(() => {
        unblockRef.current = false
      }, 100)
    }
  }

  const cancelNavigation = () => {
    nextLocation.current = null
    setIsOpen(false)
  }

  return {
    isOpen,
    confirmNavigation,
    cancelNavigation,
  }
}
